// Newsletter Popup Component
// Handles open/close behavior for the newsletter popup
if (!customElements.get("newsletter-popup")) {
  class NewsletterPopup extends HTMLElement {
    constructor() {
      super();
      this.closeButtons = [];
      this.openButtons = [];
      this.floatingWrappers = [];
      this.floatingDismissButtons = [];
      this.floatingVisible = false;
      this.floatingIdleTimer = null;
      this.floatingHideTimers = new Map();
      this.isOpen = false;
      this.openedOnce = false;
      this.overlay = null;
      this.triggerType = "time";
      this.triggerDelay = 0;
      this.triggerScroll = 0;
      this.testMode = false;
      this.intervalDays = 0;
      this.disableLoggedIn = false;
      this.storageKey = "";
      this.scrollHandler = null;
      this.timerId = null;
      this.scrollRaf = null;
      this.form = null;
      this.submitButton = null;
      this.submitLabel = null;
      this.submitLoading = null;
      this.opener = null;
      this._focusTrap = null;
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onOpenClick = this.onOpenClick.bind(this);
      this.onFloatingDismissClick = this.onFloatingDismissClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onScroll = this.onScroll.bind(this);
    }

  connectedCallback() {
    this.cacheElements();
    this.cacheSettings();
    this.bindEvents();
    this.initTrigger();
    this.initFloatingVisibility();
    if (Shopify.designMode) {
      this._onEditorSelect = this._onEditorSelect.bind(this);
      this._onEditorDeselect = this._onEditorDeselect.bind(this);
      document.addEventListener("shopify:section:select", this._onEditorSelect);
      document.addEventListener("shopify:section:deselect", this._onEditorDeselect);
    }
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.clearTriggers();
    this.teardownFloatingVisibility();
    if (Shopify.designMode) {
      document.removeEventListener("shopify:section:select", this._onEditorSelect);
      document.removeEventListener("shopify:section:deselect", this._onEditorDeselect);
    }
  }

  cacheElements() {
    this.closeButtons = Array.from(
      this.querySelectorAll("[data-newsletter-popup-close]")
    );
    const id = this.getAttribute("id");
    if (id) {
      this.openButtons = Array.from(
        document.querySelectorAll(
          `[data-newsletter-popup-open][data-newsletter-popup-target="${id}"]`
        )
      );
      this.floatingWrappers = Array.from(
        document.querySelectorAll(
          `[data-newsletter-popup-float][data-newsletter-popup-target="${id}"]`
        )
      );
      this.floatingDismissButtons = Array.from(
        document.querySelectorAll(
          `[data-newsletter-popup-float-dismiss][data-newsletter-popup-target="${id}"]`
        )
      );
    } else {
      this.openButtons = [];
      this.floatingWrappers = [];
      this.floatingDismissButtons = [];
    }
    this.overlay = this.querySelector("[data-newsletter-popup-overlay]");
    this.form = this.querySelector("form");
    this.submitButton = this.querySelector("[data-newsletter-submit]");
    this.submitLabel = this.querySelector("[data-newsletter-submit-label]");
    this.submitLoading = this.querySelector("[data-newsletter-submit-loading]");
  }

  cacheSettings() {
    this.triggerType = this.dataset.triggerType || "time";
    this.triggerDelay = Number(this.dataset.triggerDelay || 0);
    this.triggerScroll = Number(this.dataset.triggerScroll || 0);
    this.testMode = this.dataset.testMode === "true";
    this.intervalDays = Number(this.dataset.intervalDays || 0);
    this.disableLoggedIn = this.dataset.disableLoggedIn === "true";
    this.customerLoggedIn = this.dataset.customerLoggedIn === "true";
    const id = this.getAttribute("id") || "newsletter-popup";
    this.storageKey = `newsletter-popup:last-shown:${id}`;
  }

  bindEvents() {
    this.closeButtons.forEach((button) => {
      button.addEventListener("click", this.onCloseClick);
    });
    this.openButtons.forEach((button) => {
      button.addEventListener("click", this.onOpenClick);
    });
    this.floatingDismissButtons.forEach((button) => {
      button.addEventListener("click", this.onFloatingDismissClick);
    });
    this.form?.addEventListener("submit", this.onSubmit);
  }

  unbindEvents() {
    this.closeButtons.forEach((button) => {
      button.removeEventListener("click", this.onCloseClick);
    });
    this.openButtons.forEach((button) => {
      button.removeEventListener("click", this.onOpenClick);
    });
    this.floatingDismissButtons.forEach((button) => {
      button.removeEventListener("click", this.onFloatingDismissClick);
    });
    this.form?.removeEventListener("submit", this.onSubmit);
  }

  clearTriggers() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      window.removeEventListener("resize", this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  initFloatingVisibility() {
    if (!this.floatingWrappers.length) return;
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll);
    this.onScroll();
  }

  teardownFloatingVisibility() {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onScroll);
    if (this.floatingIdleTimer) {
      window.clearTimeout(this.floatingIdleTimer);
      this.floatingIdleTimer = null;
    }
    this.floatingHideTimers.forEach((timer) => window.clearTimeout(timer));
    this.floatingHideTimers.clear();
  }

  _isEditorSection(event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return false;
    return !!this.closest(`#shopify-section-${sectionId}`);
  }

  _onEditorSelect(event) {
    if (!this._isEditorSection(event)) return;
    if (!this.isOpen) this.open();
  }

  _onEditorDeselect(event) {
    if (!this._isEditorSection(event)) return;
    if (this.isOpen) this.close();
  }

  initTrigger() {
    if (this.testMode) {
      this.open();
      return;
    }
    if (!this.shouldShow()) return;
    if (this.triggerType === "scroll") {
      this.initScrollTrigger();
      return;
    }
    this.initTimeTrigger();
  }

  initTimeTrigger() {
    const delayMs = Math.max(0, this.triggerDelay) * 1000;
    this.timerId = window.setTimeout(() => {
      this.open();
    }, delayMs);
  }

  initScrollTrigger() {
    const target = Math.min(100, Math.max(0, this.triggerScroll));
    const onScroll = () => {
      if (this.isOpen || this.openedOnce) return;
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      if (scrollHeight <= 0) return;
      const percent = (scrollTop / scrollHeight) * 100;
      if (percent >= target) {
        this.open();
      }
    };
    this.scrollHandler = () => {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        onScroll();
      });
    };
    window.addEventListener("scroll", this.scrollHandler, { passive: true });
    window.addEventListener("resize", this.scrollHandler);
    this.scrollHandler();
  }

  shouldShow() {
    if (this.testMode) return true;
    if (this.disableLoggedIn && this.customerLoggedIn) return false;
    if (!this.intervalDays) return true;
    try {
      const lastShown = Number(localStorage.getItem(this.storageKey) || 0);
      if (!lastShown) return true;
      const intervalMs = this.intervalDays * 24 * 60 * 60 * 1000;
      return Date.now() - lastShown >= intervalMs;
    } catch (error) {
      return true;
    }
  }

    onCloseClick(event) {
      event.preventDefault();
      this.close();
    }

  onOpenClick(event) {
    event.preventDefault();
    this.open(event.currentTarget);
  }

  onFloatingDismissClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const wrapper = event.currentTarget?.closest(
      "[data-newsletter-popup-float]"
    );
    if (wrapper) {
      wrapper.dataset.dismissed = "true";
      this.hideFloatingWrapper(wrapper);
    }
  }

  showFloatingWrapper(wrapper) {
    if (!wrapper || wrapper.dataset.dismissed === "true") return;
    const existing = this.floatingHideTimers.get(wrapper);
    if (existing) {
      window.clearTimeout(existing);
      this.floatingHideTimers.delete(wrapper);
    }
    wrapper.classList.remove("hidden");
    requestAnimationFrame(() => {
      wrapper.dataset.visible = "true";
    });
  }

  hideFloatingWrapper(wrapper) {
    if (!wrapper) return;
    const existing = this.floatingHideTimers.get(wrapper);
    if (existing) {
      window.clearTimeout(existing);
    }
    wrapper.dataset.visible = "false";
    const timer = window.setTimeout(() => {
      wrapper.classList.add("hidden");
      this.floatingHideTimers.delete(wrapper);
    }, 300);
    this.floatingHideTimers.set(wrapper, timer);
  }

  onScroll() {
    const shouldShow = window.scrollY > 0;
    if (this.floatingVisible === shouldShow) return;
    this.floatingVisible = shouldShow;
    this.floatingWrappers.forEach((wrapper) => {
      if (wrapper.dataset.dismissed === "true") return;
      if (shouldShow) {
        this.showFloatingWrapper(wrapper);
      } else {
        this.hideFloatingWrapper(wrapper);
      }
    });
    if (this.floatingIdleTimer) {
      window.clearTimeout(this.floatingIdleTimer);
    }
    if (shouldShow) {
      this.floatingIdleTimer = window.setTimeout(() => {
        this.floatingVisible = false;
        this.floatingWrappers.forEach((wrapper) => {
          if (wrapper.dataset.dismissed === "true") return;
          this.hideFloatingWrapper(wrapper);
        });
      }, 3000);
    } else {
      this.floatingIdleTimer = null;
    }
  }

  onSubmit() {
    if (!this.submitButton || this.submitButton.disabled) return;
    this.submitButton.setAttribute("disabled", "true");
    if (this.submitLabel) {
      this.submitLabel.classList.add("hidden");
    }
    if (this.submitLoading) {
      this.submitLoading.classList.remove("hidden");
      this.submitLoading.classList.add("flex");
    }
  }

  hasBlockingModal() {
    const enquiryModal = document.querySelector("[data-enquire-modal][open]");
    if (enquiryModal) return true;
    const openModal = document.querySelector('.modal.flex[aria-hidden="false"]');
    return openModal && openModal !== this;
  }

  open(opener) {
    if (this.isOpen) return;
    if (this.hasBlockingModal()) return;
    this.opener = opener || document.activeElement;
    this.isOpen = true;
    this.openedOnce = true;
    this.classList.remove("hidden");
    this.classList.add("flex");
    this.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("overflow-hidden");
    if (this.floatingWrappers.length) {
      this.floatingWrappers.forEach((wrapper) => {
        if (wrapper.dataset.dismissed === "true") return;
        this.hideFloatingWrapper(wrapper);
      });
    } else {
      this.openButtons.forEach((button) => {
        button.classList.add("hidden");
      });
    }
    if (this.overlay) {
      this.overlay.classList.add("opacity-100");
      this.overlay.classList.remove("opacity-0");
    }
    if (this.storageKey) {
      try {
        localStorage.setItem(this.storageKey, String(Date.now()));
      } catch (error) {
        // Ignore storage write errors (private mode or blocked storage).
      }
    }
    if (!this._focusTrap) {
      this._focusTrap = webvista.createFocusTrap(this, {
        onEscape: () => this.close(),
      });
    }
    this.addEventListener("keydown", this._focusTrap);
    webvista.focusFirstElement(this);
    this.clearTriggers();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (this.overlay) {
      this.overlay.classList.remove("opacity-100");
      this.overlay.classList.add("opacity-0");
    }
    this.classList.add("hidden");
    this.classList.remove("flex");
    this.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("overflow-hidden");
    if (this._focusTrap) {
      this.removeEventListener("keydown", this._focusTrap);
    }
    if (this.opener && typeof this.opener.focus === "function") {
      this.opener.focus();
      this.opener = null;
    }
    if (this.floatingWrappers.length) {
      this.floatingWrappers.forEach((wrapper) => {
        if (wrapper.dataset.dismissed === "true") return;
        if (this.floatingVisible) {
          this.showFloatingWrapper(wrapper);
        }
      });
    } else {
      this.openButtons.forEach((button) => {
        button.classList.remove("hidden");
      });
    }
  }
  }

  customElements.define("newsletter-popup", NewsletterPopup);
}
